/**
 * Erfassung der Nutzeraktivität — App-Start, Projekt auf, Projekt zu.
 *
 * Schreibende Seite des Moduls „Nutzeraktivität" (supabase/user_activity_events.sql,
 * MIGRATIONS.md Nr. 46). Die lesende Seite (Auswertung, Admin-Ansicht) fasst
 * diese Datei nicht an; der gemeinsame Vertrag ist `userActivityTypes.ts`.
 *
 * ─── Die eine Regel, die über allem steht ──────────────────────────────────
 * TELEMETRIE DARF NIEMALS EINEN BEDIENPFAD ANHALTEN. Keine Funktion hier gibt
 * ein Promise zurück, auf das jemand warten müsste, keine wirft, keine
 * blockiert. Fällt die Erfassung aus — Netz weg, Tabelle noch nicht
 * ausgerollt, sessionStorage gesperrt —, merkt der Anwender davon nichts und
 * arbeitet unverändert weiter. Eine fehlende Statistikzeile ist ein
 * Schönheitsfehler, ein hängender Projektwechsel ein Betriebsschaden.
 *
 * ─── Was NICHT erfasst wird ────────────────────────────────────────────────
 * Kein Standort, kein `navigator.geolocation`, kein Bewegungsprofil. Die
 * Karte der Auswertung verbindet ADRESSEN GEÖFFNETER PROJEKTE, nicht den
 * Aufenthaltsort des Nutzers.
 *
 * ─── Die Wache gegen doppelte Schließen-Ereignisse ─────────────────────────
 * Aus einem Projekt führen mehrere Wege gleichzeitig hinaus: der
 * Zurück-Knopf, der Leerlauf-Rückzug, das Abmelden und das Schließen des
 * Tabs — und zwei davon feuern regelmäßig hintereinander (erst
 * `handleLeaveProject`, gleich darauf `pagehide`). Ohne Wache stünden für ein
 * einziges Öffnen zwei oder drei Schließen-Zeilen in der Tabelle, und die
 * Paarbildung der Auswertung machte daraus eine falsche Verweildauer plus
 * verworfene Reste.
 *
 * Deshalb merkt sich dieses Modul GENAU EIN offenes Projekt (`openProject`).
 * Ein Schließen wird nur geschrieben, wenn dieses Merkzeichen gesetzt ist —
 * und es wird VOR dem Absenden geleert, exakt wie `releaseHeldLock` in
 * App.tsx den Sperr-Ref leert, bevor der RPC hinausgeht. Der zweite Weg
 * findet dann nichts mehr vor und schreibt auch nichts. Dasselbe Merkzeichen
 * trägt den Projektwechsel: liegt beim Öffnen noch ein Projekt offen, geht
 * zuerst ein `switch`-Schließen hinaus.
 *
 * ─── Der Nutzerwechsel im selben Tab ───────────────────────────────────────
 * Meldet sich A ab und B im selben Browser-Tab an, ist das für den Browser
 * dieselbe Sitzung — für die Statistik sind es zwei. Deshalb hängt die
 * Startmarke an der BENUTZER-ID statt an einem bloßen „schon erledigt": B
 * bekäme sonst für diese Browser-Sitzung kein `app_start`, und damit wären
 * erster Start, letzter Start und Startzahl von B falsch — also genau die
 * Kennzahlen, für die es dieses Modul gibt. Beim Wechsel fällt zusätzlich
 * alles weg, was am Vorgänger hing: Sitzungs-ID, offenes Projekt und
 * wiederherstellbares Projekt (`applyUserSwitch`).
 *
 * F5 bleibt davon unberührt — dort ist die Benutzer-ID dieselbe, die Marke
 * passt, und es bleibt bei genau einem Start je Browser-Sitzung.
 *
 * ─── Das Ende, das nie gemeldet wird (mobil) ───────────────────────────────
 * Auf dem Handy verlässt fast niemand ein Projekt über den Zurück-Knopf. Man
 * wechselt in die Kamera, ans Telefon, in die Karte — oder sperrt den
 * Bildschirm, und irgendwann räumt das Betriebssystem die Seite ab. Gemessen
 * an echten Daten: bei einem Außendienst-Konto standen 14 von 19 Sitzungen
 * dauerhaft als „offen", jeder Start hatte eine eigene `session_id` (also
 * jedes Mal ein Kaltstart), und es gab NULL Rückkehren aus dem
 * Back-Forward-Cache — die Seite fror nicht ein, sie starb.
 *
 * Dagegen stehen hier drei Dinge, in dieser Reihenfolge der Wirkung:
 *
 * 1. `recordProjectHidden` / `recordProjectVisible` — der Wechsel in den
 *    Hintergrund ist mobil das eigentliche Verlassen, und er kommt, WÄHREND
 *    die Seite noch lebt: ein gewöhnlicher Insert reicht, kein Beacon-Glück.
 *    Erst nach einer Karenzzeit (HIDDEN_GRACE_MS) wird geschlossen, sonst
 *    zerhackte jeder Tabwechsel am Schreibtisch die Sitzungen in Schnipsel.
 *    Geschrieben wird dann auf den Zeitpunkt des WEGWECHSELS, nicht auf den
 *    des Weckers.
 * 2. Das Journal im localStorage (`PENDING_KEY`) — was offen ist und wann es
 *    zuletzt gelebt hat. Es überlebt den Kaltstart (sessionStorage nicht),
 *    und der nächste App-Start trägt daraus nach, was fehlt.
 * 3. Der Herzschlag (`HEARTBEAT_MS`) — nur für den Fall, dass die Seite ohne
 *    jedes Ereignis stirbt. Er kostet einen localStorage-Schreibvorgang, kein
 *    Netz.
 *
 * Die Grundregel bleibt unangetastet: nachgetragen wird ausschließlich mit
 * einem Zeitpunkt, den die App wirklich gesehen hat, nie mit „jetzt" und nie
 * mit einem gerechneten Wert. Der ungenaueste Fall (Herzschlag, bis zu
 * HEARTBEAT_MS zu früh) trägt dafür einen eigenen Grund: 'recovered'.
 * Entschieden wird das pur in `userActivityRecovery.ts`.
 */

import { supabase } from './supabaseClient';
import type { UserActivityCloseReason, UserActivityOpenReason } from './userActivityTypes';
import type { PendingActivityClose } from './userActivityRecovery';
import {
    decideRecovery, parsePendingList, serializePendingList, upsertPending, removePending,
} from './userActivityRecovery';

// sessionStorage, nicht localStorage: die Sitzungs-ID soll F5 überleben und
// mit dem Tab sterben — dieselbe Abwägung wie bei NAV_STATE_KEY
// (navigationPersistence.ts). Zwei parallele Tabs sind zwei Sitzungen, und
// das ist richtig so: sie öffnen und schließen Projekte unabhängig.
const SESSION_ID_KEY = 'osia_activity_session_v1';
/**
 * Der Wert dieser Marke ist die BENUTZER-ID, für die der Start gemeldet wurde
 * — nicht bloß ein Ja/Nein (siehe „Der Nutzerwechsel im selben Tab" im
 * Dateikopf). Darum `v2`: ein aus der Vorversion stehengebliebenes '1' passt
 * zu keiner Benutzer-ID und führt beim ersten Profil-Laden zu genau dem, was
 * richtig ist — einem Start für den gerade angemeldeten Nutzer.
 */
const APP_START_KEY = 'osia_activity_started_v2';

/**
 * Das Journal des offenen Projekts — hier ausdrücklich `localStorage`.
 *
 * Es soll GENAU DEN Kaltstart überleben, an dem `sessionStorage` leer ist:
 * weggewischte App, vom System abgeräumter Tab, Absturz. Ein Journal im
 * sessionStorage wäre in dem Moment mit weg, in dem man es braucht.
 * Höchstens ein Eintrag, wie es höchstens ein offenes Projekt gibt.
 */
const PENDING_KEY = 'osia_activity_pending_v1';
/**
 * Seit 07.09.2026 EIN Platz JE SITZUNG statt eines globalen (Begruendung in
 * userActivityRecovery.ts, „Das Journal hat MEHRERE Plaetze"). Der alte
 * Schluessel wird beim ersten Start noch gelesen und danach geraeumt.
 */
const PENDING_LIST_KEY = 'osia_activity_pending_v2';

/**
 * Abstand des Herzschlags. Die Zahl ist die Obergrenze des Fehlers im
 * schlechtesten Fall (Seite stirbt ohne jedes Ereignis) — dann fehlt der
 * Sitzung am Ende bis zu diese Spanne, und die Zeile trägt 'recovered'.
 * 30 s sind der Kompromiss: fein genug, um niemandem eine Viertelstunde zu
 * unterschlagen, grob genug, um im Hintergrund nicht aufzufallen.
 */
const HEARTBEAT_MS = 30_000;

/**
 * Karenzzeit, bevor ein Wechsel in den Hintergrund als Verlassen gilt.
 *
 * Ohne sie schlösse jeder Tabwechsel am Schreibtisch und jeder Blick in eine
 * andere App das Projekt — die Statistik zerfiele in Schnipsel von
 * Sekundenlänge, und „Ø Verweildauer" wäre nichts mehr wert. Wer innerhalb
 * dieser Spanne zurückkommt, arbeitet in DERSELBEN Sitzung weiter; es wird
 * gar nichts geschrieben.
 */
const HIDDEN_GRACE_MS = 60_000;

interface OpenProjectRef {
    projectId: string;
    projectNumber: string | null;
    projectName: string | null;
}

/**
 * Das eine offene Projekt (siehe Wache im Dateikopf). `null` heißt: es gibt
 * nichts zu schließen.
 */
let openProject: OpenProjectRef | null = null;

/**
 * Rückfallebene, falls `sessionStorage` nicht benutzbar ist (privater Modus,
 * gesperrte Website-Daten). Dann leben Sitzungs-ID und Startmarke nur im
 * Speicher und überleben F5 nicht — das erzeugt einen zusätzlichen App-Start
 * und ist ausdrücklich die bessere Hälfte des Übels gegenüber gar keiner
 * Erfassung.
 */
let memorySessionId: string | null = null;
let memoryAppStartUser: string | null = null;

/**
 * Das beim Entladen geschlossene Projekt — aufgehoben für den Fall, dass die
 * Seite gar nicht gestorben ist (Back-Forward-Cache, siehe
 * `recordProjectRestore`). Wird beim Wiederauftauchen genau einmal
 * eingelöst und dabei geleert.
 */
let restorableProject: OpenProjectRef | null = null;

/**
 * Der angemeldete Nutzer, wie ihn `recordAppStart` zuletzt gesehen hat.
 * Gebraucht wird er allein fürs Journal: ein nachgetragenes Schließen läuft
 * über einen gewöhnlichen Insert und landet damit unter `auth.uid()` des
 * GERADE Angemeldeten — ein fremder Eintrag dürfte also nie geschrieben
 * werden (Prüfung in `decideRecovery`).
 */
let currentUserId: string | null = null;

/** Herzschlag, läuft nur solange ein Projekt offen ist. */
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

/** Wecker der Karenzzeit nach dem Wechsel in den Hintergrund. */
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Letzter vergebener `occurred_at`-Zeitpunkt in Millisekunden.
 *
 * Die Paarbildung sortiert nach `occurred_at`. Ein `switch`-Schließen und das
 * unmittelbar folgende Öffnen fallen aber leicht in dieselbe Millisekunde;
 * bei Gleichstand entschiede dann die zufällige uuid darüber, ob das neue
 * Projekt vor dem alten geschlossen wird. Deshalb ist die Folge innerhalb
 * einer Sitzung streng monoton: gleicher oder rückwärts gehender Zeitpunkt
 * wird um eine Millisekunde angehoben.
 */
let lastOccurredMs = 0;

const nextOccurredAt = (): string => {
    const now = Date.now();
    lastOccurredMs = now > lastOccurredMs ? now : lastOccurredMs + 1;
    return new Date(lastOccurredMs).toISOString();
};

const readSessionStorage = (key: string): string | null => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeSessionStorage = (key: string, value: string): void => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        /* Website-Daten gesperrt — die Rückfallebene im Speicher übernimmt. */
    }
};

// ─── Journal (localStorage) ────────────────────────────────────────────────

/**
 * Spiegel des Journals im Speicher. Gelesen wird von hier, solange der Tab
 * lebt; geschrieben wird immer BEIDES. Sind die Website-Daten gesperrt,
 * bleibt nur der Spiegel — dann wirkt die Karenzzeit weiter, nur der Nachtrag
 * nach einem Kaltstart fällt aus. Das ist dieselbe Abwägung wie bei der
 * Sitzungs-ID: lieber die halbe Erfassung als gar keine.
 */
let pending: PendingActivityClose | null = null;

const readLocalStorage = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeLocalStorage = (key: string, value: string): void => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* Website-Daten gesperrt — nur der Spiegel im Speicher trägt dann. */
    }
};

const removeLocalStorage = (key: string): void => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* egal */
    }
};

/** Alle Plaetze des Journals, Alt-Journal (v1) eingeschlossen. */
const readPendingList = (): PendingActivityClose[] => {
    const liste = parsePendingList(readLocalStorage(PENDING_LIST_KEY));
    const alt = parsePendingList(readLocalStorage(PENDING_KEY));
    for (const e of alt) if (!liste.some(x => x.sessionId === e.sessionId)) liste.push(e);
    return liste;
};

const writePendingList = (liste: PendingActivityClose[]): void => {
    writeLocalStorage(PENDING_LIST_KEY, serializePendingList(liste));
    removeLocalStorage(PENDING_KEY);
};

const writePending = (eintrag: PendingActivityClose): void => {
    pending = eintrag;
    writePendingList(upsertPending(readPendingList(), eintrag));
};

/** Einzelne Felder nachziehen. Ohne offenen Eintrag passiert nichts. */
const patchPending = (patch: Partial<PendingActivityClose>): void => {
    if (!pending) return;
    writePending({ ...pending, ...patch });
};

const clearPending = (): void => {
    const eigene = pending?.sessionId ?? getActivitySessionId();
    pending = null;
    writePendingList(removePending(readPendingList(), eigene));
};

/**
 * uuid v4. `crypto.randomUUID` gibt es erst ab Safari 15.4 und nur im
 * sicheren Kontext; der Rückfall baut dieselbe Form aus `getRandomValues`
 * und, falls auch das fehlt, aus `Math.random`. Die Form MUSS stimmen —
 * `session_id` ist in der Datenbank eine uuid-Spalte, alles andere fliegt
 * beim Insert heraus.
 */
const createUuid = (): string => {
    try {
        const c: any = (globalThis as any).crypto;
        if (c && typeof c.randomUUID === 'function') return c.randomUUID();
        if (c && typeof c.getRandomValues === 'function') {
            const bytes = c.getRandomValues(new Uint8Array(16));
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex: string[] = [];
            for (let i = 0; i < 16; i += 1) hex.push(bytes[i].toString(16).padStart(2, '0'));
            return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
        }
    } catch {
        /* fällt auf Math.random durch */
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Die uuid dieser Browser-Sitzung. Beim ersten Aufruf erzeugt, danach aus
 * dem sessionStorage — F5 behält sie, ein neuer Tab bekommt eine eigene.
 */
export const getActivitySessionId = (): string => {
    const stored = readSessionStorage(SESSION_ID_KEY);
    if (stored) {
        memorySessionId = stored;
        return stored;
    }
    if (memorySessionId) return memorySessionId;
    const created = createUuid();
    memorySessionId = created;
    writeSessionStorage(SESSION_ID_KEY, created);
    return created;
};

/**
 * Der Nutzer, für den in diesem Tab zuletzt ein `app_start` geschrieben wurde
 * — oder `null`, wenn noch keiner geschrieben wurde. Die Marke liegt im
 * sessionStorage (überlebt F5) mit der Rückfallebene im Speicher.
 */
const markedActivityUser = (): string | null =>
    readSessionStorage(APP_START_KEY) || memoryAppStartUser || null;

/**
 * Anmeldung eines ANDEREN Nutzers im selben Tab.
 *
 * Geschnitten wird alles, was am Vorgänger hing:
 * - die Startmarke, damit der neue Nutzer seinen eigenen `app_start` bekommt
 *   (ohne das ist genau die Kennzahl kaputt, für die es das Modul gibt);
 * - die Sitzungs-ID, damit eine `session_id` weiterhin genau einem Nutzer
 *   gehört. Die Paarung der Auswertung filtert zwar vor dem Gruppieren nach
 *   Nutzer (`summarizeUserActivity`) und käme auch mit einer geteilten ID
 *   klar — diese Zusicherung liegt aber in einer fremden Datei, und die
 *   Tabelle hat einen Index über (session_id, occurred_at), der zu genau
 *   solchen Auswertungen einlädt. Die Erneuerung kostet nichts und macht die
 *   Zusicherung hier vor Ort wahr;
 * - das offene und das wiederherstellbare Projekt, damit der neue Nutzer
 *   weder ein `switch`-Schließen für ein fremdes Projekt anhängt noch dessen
 *   Projekt über `pageshow` wieder aufnimmt.
 *
 * Für den Vorgänger wird hier BEWUSST kein Schließen mehr geschrieben: das
 * Token gehört in diesem Moment schon dem neuen Nutzer, die Zeile landete
 * unter DESSEN Benutzer-ID und erfände dort ein Schließen ohne Öffnen. Die
 * Sitzung des Vorgängers bleibt in der Auswertung offen stehen — die ehrliche
 * Lücke, die dieses Modul überall der geschätzten Dauer vorzieht. Den
 * regulären Weg mit gültigem Token geht das Abmelden selbst (`handleLogout`),
 * lange bevor es hierher kommt.
 *
 * Idempotent, weil beide Auslöser hier durchlaufen: die Auth-Meldung meldet
 * den Wechsel sofort, `recordAppStart` fängt ihn auch dann, wenn die Meldung
 * ausbleibt. Wer zuerst kommt, räumt auf; der zweite findet eine leere Marke
 * vor und tut nichts mehr.
 */
const applyUserSwitch = (): void => {
    openProject = null;
    restorableProject = null;
    stopHeartbeat();
    if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
    }
    // Das Journal bleibt BEWUSST liegen: es gehört dem Vorgänger, und nur er
    // darf es nachtragen (der Insert liefe sonst unter der neuen `auth.uid()`
    // und hängte seine Arbeitszeit dem Nachfolger an). `decideRecovery` weist
    // es beim nächsten Start des Nachfolgers ab, ohne es wegzuwerfen; meldet
    // sich der Vorgänger auf diesem Gerät wieder an, holt er es sich.
    pending = null;
    memoryAppStartUser = null;
    writeSessionStorage(APP_START_KEY, '');
    const created = createUuid();
    memorySessionId = created;
    writeSessionStorage(SESSION_ID_KEY, created);
};

/**
 * Die Spalten, die der Client schreibt — und NUR die.
 *
 * `user_id` und `company_id` fehlen hier mit Absicht: beide setzt die
 * Datenbank selbst (Spaltenvorgaben `auth.uid()` und `current_company_id()`).
 * Wer sie hier wieder einführt, macht den Riegel aus
 * supabase/user_activity_events.sql wieder auf — siehe `send`.
 */
interface ActivityEventInsert {
    session_id: string;
    event_type: 'app_start' | 'project_open' | 'project_close';
    occurred_at: string;
    project_id?: string;
    project_number?: string | null;
    project_name?: string | null;
    close_reason?: UserActivityCloseReason;
    open_reason?: UserActivityOpenReason;
}

/**
 * Der einzige Schreibweg im Normalbetrieb.
 *
 * WAS DER CLIENT NICHT SETZT, KANN ER AUCH NICHT FALSCH SETZEN. Deshalb
 * fehlen zwei Spalten in jedem Datensatz, den dieses Modul absendet — die
 * Datenbank trägt sie selbst ein:
 *
 * - `user_id` — Spaltenvorgabe `auth.uid()`;
 * - `company_id` — Spaltenvorgabe `current_company_id()` (liest die Firma
 *   beim Insert aus `profiles`).
 *
 * Bei `company_id` ist das seit der Härtung der Lese-Policy kein Feinschliff
 * mehr, sondern die Schließung eines P1: die SELECT-Policy macht die
 * Mandantentrennung an dieser Spalte fest. Ein vom Client gesetzter Wert
 * hieße, dass jeder mit einer geratenen fremden Firmen-ID (fünfstelliger
 * Zifferncode) erfundene Arbeitszeiten und Projektnamen in die Auswertung
 * einer FREMDEN Firma schreiben kann — unter seiner echten Benutzer-ID, also
 * ohne aufzufallen.
 *
 * Die INSERT-Policy weist eine abweichende Firma zusätzlich ab
 * (`company_id::text is not distinct from (select public.current_company_id())`).
 * Dieser Weg schickt sie erst gar nicht mit — zwei Riegel vor derselben Tür.
 */
const send = (row: ActivityEventInsert): void => {
    try {
        void Promise.resolve(supabase.from('user_activity_events').insert(row))
            .then((res: any) => {
                if (res?.error) console.warn('Aktivitaet nicht erfasst', res.error);
            })
            .catch(console.warn);
    } catch (e) {
        // Selbst der synchrone Teil des Aufbaus darf nichts nach außen lassen.
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

// ─── Herzschlag ────────────────────────────────────────────────────────────
//
// Er geht NICHT ins Netz. Er schreibt einen Zeitstempel ins Journal, damit
// eine Seite, die ohne jedes Ereignis stirbt, wenigstens ein belegtes Ende
// hinterlässt. Alles andere wäre eine Schätzung.

const stopHeartbeat = (): void => {
    if (!heartbeatTimer) return;
    try {
        clearInterval(heartbeatTimer);
    } catch {
        /* egal */
    }
    heartbeatTimer = null;
};

const startHeartbeat = (): void => {
    stopHeartbeat();
    try {
        heartbeatTimer = setInterval(() => {
            try {
                if (!openProject) {
                    stopHeartbeat();
                    return;
                }
                patchPending({ lastSeenAt: new Date().toISOString() });
            } catch {
                /* stumm — Telemetrie hält keinen Bedienpfad an */
            }
        }, HEARTBEAT_MS);
    } catch {
        /* kein Timer verfügbar — dann eben ohne Herzschlag */
    }
};

// ─── Der Nachtrag beim Start ───────────────────────────────────────────────

/**
 * Ein liegengebliebenes Journal einlösen.
 *
 * Läuft ganz am Anfang von `recordAppStart` — VOR der Startmarke und damit
 * auch nach einem F5, bei dem die Marke passt und die Funktion sonst sofort
 * zurückkehrt. Sonst überschriebe das Wiederherstellen der Ansicht
 * (`loadNavState` → `recordProjectOpen`) das Journal, bevor jemand es gelesen
 * hat, und das Schließen der Vorgängersitzung wäre endgültig weg.
 *
 * ─── Warum vorher gefragt wird, ob es die Zeile schon gibt ─────────────────
 * Der keepalive-Beacon beim Entladen KANN angekommen sein — der Client
 * erfährt das nicht mehr, der Tab ist da schon tot. Ohne Nachfrage stünden
 * für ein Öffnen zwei Schließen in der Tabelle; die Paarung nähme das erste
 * und verwürfe das zweite (`discardedCloseCount`), also ein selbstgemachter
 * Befund in einer Statistik, die Befunde melden soll. Eine Zeile lesen ist
 * billiger als eine falsche schreiben.
 *
 * Schlägt die Nachfrage fehl, wird NICHTS geschrieben: im Zweifel bleibt die
 * Sitzung offen stehen. Das ist die ehrliche Lücke, die dieses Modul überall
 * der erfundenen Zahl vorzieht.
 */
const flushPendingClose = (userId: string): void => {
    try {
        const jetzt = new Date().toISOString();
        const eigeneSitzung = getActivitySessionId();
        let liste = readPendingList();
        if (liste.length === 0) return;

        for (const eintrag of liste) {
            // Die eigene Sitzung mit offenem Projekt ist kein Nachtrag, sie lebt.
            if (openProject && eintrag.sessionId === eigeneSitzung) continue;

            const entscheidung = decideRecovery(eintrag, userId, jetzt);
            if (entscheidung.kind === 'keep') continue;
            if (entscheidung.kind === 'drop') {
                liste = removePending(liste, eintrag.sessionId, eintrag.openedAt);
                continue;
            }
            nachtragen(eintrag, entscheidung.occurredAt, entscheidung.reason);
        }
        writePendingList(liste);
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

/**
 * Ein Journaleintrag wird zur `project_close`-Zeile — oder stellt sich als
 * schon erledigt heraus (der Beacon kam doch an). Der Eintrag verlaesst das
 * Journal ERST, wenn eines von beidem sicher ist: vorher raeumen hiesse, bei
 * einem Netzfehler die einzige Spur wegzuwerfen; der naechste Start
 * versucht es sonst noch einmal.
 *
 * DIE VORABFRAGE VERGLEICHT DIE ZEIT (07.09.2026). Bis dahin fragte sie nur
 * „gibt es fuer Sitzung+Projekt irgendeine Schliesszeile" — und die gab es
 * fast immer: Projekt auf, App-Wechsel (`hidden` geschrieben), zurueck
 * (Wiederaufnahme, gleiche Sitzung, gleiches Projekt), Seite stirbt. Der
 * Nachtrag fand die Schliesszeile der ERSTEN Oeffnung, hielt die zweite fuer
 * zu und warf das Journal weg. Flottenweit 68 von 93 offenen Sitzungen seit
 * dem 26.08., bei einem Konto 42 von 45. Gefragt wird deshalb nur nach
 * Schliesszeilen AB der Oeffnung dieses Eintrags.
 */
const nachtragen = (
    eintrag: PendingActivityClose,
    occurredAt: string,
    reason: UserActivityCloseReason,
): void => {
    const erledigt = (): void => {
        writePendingList(removePending(readPendingList(), eintrag.sessionId, eintrag.openedAt));
    };
    void Promise.resolve(
        supabase
            .from('user_activity_events')
            .select('id')
            .eq('session_id', eintrag.sessionId)
            .eq('project_id', eintrag.projectId)
            .eq('event_type', 'project_close')
            .gte('occurred_at', eintrag.openedAt)
            .limit(1),
    )
        .then((res: any) => {
            if (res?.error) return;
            if (Array.isArray(res?.data) && res.data.length > 0) {
                erledigt();
                return;
            }
            return Promise.resolve(
                supabase.from('user_activity_events').insert({
                    session_id: eintrag.sessionId,
                    event_type: 'project_close',
                    occurred_at: occurredAt,
                    project_id: eintrag.projectId,
                    project_number: eintrag.projectNumber,
                    project_name: eintrag.projectName,
                    close_reason: reason,
                }),
            ).then((ins: any) => {
                if (ins?.error) {
                    console.warn('Aktivitaet nicht erfasst', ins.error);
                    return;
                }
                erledigt();
            });
        })
        .catch(() => { /* im Zweifel nichts schreiben, Eintrag bleibt liegen */ });
};

/**
 * Genau EIN `app_start` je Browser-Sitzung UND Nutzer.
 *
 * Die Marke liegt neben der Sitzungs-ID im sessionStorage: F5 lädt die App
 * komplett neu und liefe sonst in einen zweiten Start, der in der Statistik
 * wie ein zweiter Arbeitsbeginn aussähe. Ein neuer Tab ist dagegen eine neue
 * Sitzung und darf einen eigenen Start melden.
 *
 * Die Marke trägt die Benutzer-ID, nicht bloß ein „erledigt". Meldet sich im
 * selben Tab ein anderer an, passt sie nicht mehr — er bekommt seinen eigenen
 * Start, und `applyUserSwitch` schneidet vorher alles ab, was am Vorgänger
 * hing. Beim Wiederanmelden DESSELBEN Nutzers bleibt es bei einem Start: das
 * ist dieselbe Browser-Sitzung derselben Person.
 *
 * Aufrufstelle: `fetchProfile` in App.tsx, direkt nach `setUserProfile`.
 *
 * Die Firma spielt dabei KEINE Rolle mehr: sie kommt seit der Härtung der
 * INSERT-Policy vom Server (Spaltenvorgabe `current_company_id()`) und nicht
 * mehr aus diesem Profil. Gebraucht wird hier nur noch die Benutzer-ID, und
 * zwar für die Startmarke — sie unterscheidet den Nutzerwechsel im selben Tab
 * von einem F5. `company_id` darf im Profil weiter mitkommen (die Aufrufstelle
 * übergibt schlicht das ganze Profil); gelesen wird das Feld hier nicht mehr.
 */
export const recordAppStart = (profile: { id?: string; company_id?: string | null } | null | undefined): void => {
    try {
        // VOR der Marke und vor jedem Mengengerüst: das Zugangstoken vorwärmen
        // (Begründung bei `warmBeaconAuth`) und damit zugleich das Zuhören auf
        // Auth-Meldungen aufsetzen. Das steht bewusst oberhalb des Abbruchs —
        // nach F5 kehrt diese Funktion sofort zurück, und ohne das Abo bliebe
        // der Tab für den Rest seines Lebens taub für einen Nutzerwechsel.
        warmBeaconAuth();
        // Ohne Benutzer-ID (dürfte nicht vorkommen: das Profil wird über genau
        // diese ID geladen) bliebe nur eine Marke ohne Aussage. Dann lieber
        // ein fester Platzhalter als eine Marke, die zu jedem passt.
        const userKey = profile?.id || 'unknown';
        currentUserId = userKey;
        // VOR der Startmarke: nach einem F5 kehrt diese Funktion gleich
        // zurück, das Journal der Vorgängersitzung wäre dann nie gelesen
        // worden und würde vom Wiederherstellen der Ansicht überschrieben.
        flushPendingClose(userKey);
        const marked = markedActivityUser();
        if (marked === userKey) return;
        // Eine fremde Marke heißt: hier war eben noch jemand anderes.
        if (marked) applyUserSwitch();
        memoryAppStartUser = userKey;
        writeSessionStorage(APP_START_KEY, userKey);
        send({
            session_id: getActivitySessionId(),
            event_type: 'app_start',
            occurred_at: nextOccurredAt(),
        });
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

export interface ProjectActivityContext {
    projectId: string;
    projectNumber?: string | null;
    projectName?: string | null;
    /**
     * WIRD IGNORIERT. Die Firma setzt seit der Härtung die Datenbank selbst
     * (Spaltenvorgabe `current_company_id()`, siehe `send`) — ein hier
     * übergebener Wert geht nirgendwohin. Das Feld bleibt nur stehen, damit
     * die Aufrufstellen unverändert weiterlaufen.
     */
    companyId?: string | null;
}

/**
 * Projekt geöffnet.
 *
 * Aufrufstelle: `handleOpenProject` in App.tsx, NUR im Erfolgsfall (nach
 * `setCurrentProjectId`). Der Fehlerpfad kehrt vorher mit `return false`
 * zurück und lässt den Anwender im BISHERIGEN Projekt stehen — ein Öffnen
 * dort zu melden, wäre schlicht falsch.
 *
 * Liegt noch ein Projekt offen, geht zuerst ein `switch`-Schließen hinaus.
 * Das ist der Projektwechsel: wer B öffnet, ist in A nicht mehr drin.
 * Schreibgeschützt geöffnete Projekte werden mitgezählt — Ansehen ist
 * Nutzung.
 *
 * `openReason` bleibt für jeden Aufruf aus der Oberfläche bei 'normal' —
 * Klick in der Projektliste, im Dashboard, in der Karte, aus einer Rechnung
 * heraus. 'resume' setzen nur die beiden automatischen Wege, bei denen die
 * Anwendung wiederherstellt, was der Anwender längst offen hatte:
 * `recordProjectRestore` (Rückkehr aus dem Hintergrund, bfcache) und der
 * Wiederherstellungs-Effekt in App.tsx nach einem Neuladen der Seite (F5,
 * Reload des Versions-Gates, wiederhergestellter Tab) — dort reicht
 * `handleOpenProject` den Grund als drittes Argument durch. Ohne diese
 * Unterscheidung erhöhte jedes Neuladen „Projekte geöffnet",
 * Stundenhistogramm und Heatmap. Der Wert wird IMMER mitgeschickt,
 * auch der Vorgabewert: `null` bedeutet in der Auswertung zwar dasselbe wie
 * 'normal', dann wäre aber nicht mehr unterscheidbar, ob eine Zeile aus der
 * Zeit vor dieser Spalte stammt oder ob die Erfassung den Grund verloren hat.
 */
export const recordProjectOpen = (
    ctx: ProjectActivityContext,
    openReason: UserActivityOpenReason = 'normal',
): void => {
    try {
        if (!ctx?.projectId) return;
        if (openProject) recordProjectClose({ reason: 'switch' });
        // Token für den Beacon frisch halten: ab hier gibt es etwas zu
        // schließen, und beim Entladen ist für ein Promise keine Zeit mehr.
        warmBeaconAuth();
        openProject = {
            projectId: ctx.projectId,
            projectNumber: ctx.projectNumber ?? null,
            projectName: ctx.projectName ?? null,
        };
        const sessionId = getActivitySessionId();
        const occurredAt = nextOccurredAt();
        send({
            session_id: sessionId,
            event_type: 'project_open',
            occurred_at: occurredAt,
            project_id: openProject.projectId,
            project_number: openProject.projectNumber,
            project_name: openProject.projectName,
            open_reason: openReason,
        });
        // Ab hier gibt es etwas nachzutragen, falls die Seite stirbt, ohne
        // sich zu verabschieden. Ohne bekannten Nutzer wird KEIN Journal
        // geführt: der Nachtrag liefe später unter `auth.uid()` des dann
        // Angemeldeten und könnte einer fremden Person Arbeitszeit anhängen.
        if (currentUserId) {
            writePending({
                v: 1,
                userId: currentUserId,
                sessionId,
                projectId: openProject.projectId,
                projectNumber: openProject.projectNumber,
                projectName: openProject.projectName,
                openedAt: occurredAt,
                lastSeenAt: occurredAt,
                hiddenAt: null,
                beaconAt: null,
            });
            startHeartbeat();
        }
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

export interface ProjectCloseInput {
    /**
     * Ohne Angabe wird das gemerkte offene Projekt geschlossen. Weicht die
     * angegebene ID vom gemerkten Projekt ab, passiert GAR NICHTS: dann
     * kommt der Aufruf zu einem Öffnen, das nie erfasst wurde, und ein
     * Schließen ohne Öffnen erfände eine Sitzung.
     */
    projectId?: string | null;
    projectNumber?: string | null;
    projectName?: string | null;
    /** WIRD IGNORIERT — siehe `ProjectActivityContext.companyId`. */
    companyId?: string | null;
    reason: UserActivityCloseReason;
    /**
     * Zeitpunkt des Verlassens, falls er NICHT der Augenblick dieses Aufrufs
     * ist. Genau ein Fall braucht das: der Wechsel in den Hintergrund wird
     * erst nach der Karenzzeit — oder gar erst bei der Rückkehr — zur Zeile,
     * gehört aber auf den Augenblick des Wegwechsels. Ohne diesen Weg zählte
     * die Karenzzeit als Arbeitszeit mit.
     */
    occurredAt?: string;
}

/**
 * Die Zeile hinausschicken und das Journal nachziehen — der gemeinsame Boden
 * aller Schließen-Wege.
 *
 * `journal: 'clear'` heißt: dieser Weg ist verlässlich (die Seite lebt), das
 * Journal hat seinen Zweck erfüllt. `'stamp'` heißt: der Beacon ist zwar
 * hinaus, ob er ankommt, weiß niemand — der Eintrag bleibt liegen und trägt
 * den Zeitpunkt, damit der nächste Start ihn nachtragen kann, falls die Zeile
 * fehlt.
 */
const emitClose = (
    held: OpenProjectRef,
    reason: UserActivityCloseReason,
    occurredAt: string,
    journal: 'clear' | 'stamp',
    projectNumber?: string | null,
    projectName?: string | null,
): void => {
    stopHeartbeat();
    if (journal === 'clear') clearPending();
    else patchPending({ beaconAt: occurredAt });
    send({
        session_id: getActivitySessionId(),
        event_type: 'project_close',
        occurred_at: occurredAt,
        project_id: held.projectId,
        project_number: projectNumber ?? held.projectNumber,
        project_name: projectName ?? held.projectName,
        close_reason: reason,
    });
};

/**
 * Projekt geschlossen — höchstens einmal je Öffnen (Wache im Dateikopf).
 *
 * Nummer und Name stammen aus dem gemerkten Öffnen und müssen nicht erneut
 * mitgegeben werden; wer sie doch mitgibt, überschreibt sie. Die Firma ist
 * hier gar kein Thema mehr — sie kommt vom Server (siehe `send`).
 */
export const recordProjectClose = (input: ProjectCloseInput): void => {
    try {
        const held = openProject;
        if (!held) return;
        if (input.projectId && input.projectId !== held.projectId) return;
        // ERST das Merkzeichen leeren, DANN senden — sonst gäbe ein zweiter
        // Weg aus dem Projekt (Zurück-Knopf und gleich darauf pagehide) die
        // Zeile ein zweites Mal ab.
        openProject = null;
        emitClose(
            held,
            input.reason,
            input.occurredAt || nextOccurredAt(),
            'clear',
            input.projectNumber,
            input.projectName,
        );
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

// ─── Beacon: das Schließen beim Zumachen des Tabs ──────────────────────────
//
// Der Beacon geht an PostgREST vorbei am supabase-Client vorbei — er baut den
// Datensatz von Hand. Auch er schickt deshalb weder `user_id` noch
// `company_id` mit: beide Spaltenvorgaben greifen serverseitig genauso, und
// die INSERT-Policy prüft auch diesen Weg. Ein handgebauter Body ist die
// Stelle, an der eine gefälschte Firma sonst am leichtesten wieder
// hereinkäme.
//
// Ein gewöhnlicher supabase-Insert überlebt `pagehide` nicht zuverlässig: der
// Browser bricht offene Anfragen beim Entladen ab. `navigator.sendBeacon`
// scheidet aus, weil sich damit keine Kopfzeilen setzen lassen — PostgREST
// braucht aber `apikey` UND `Authorization`. Bleibt `fetch(..., { keepalive:
// true })`: dieselbe Anfrage, nur mit der Zusage des Browsers, sie nach dem
// Entladen zu Ende zu bringen.
//
// Der Haken ist das Zugangstoken. `supabase.auth.getSession()` ist
// asynchron, und beim Entladen bleibt für ein Promise keine Zeit mehr.
// Deshalb wird es vorgewärmt — beim App-Start und bei jedem Öffnen eines
// Projekts, also immer dann, wenn es später etwas zu schließen geben könnte
// — und danach über `onAuthStateChange` frisch gehalten, solange der Tab
// lebt (Begründung bei `subscribeAuthChanges`).
// Reicht das Token nicht (abgelaufen, nie geholt), geht die Zeile verloren:
// die Sitzung bleibt in der Auswertung „offen" stehen und wird NICHT
// geschätzt. Genau das ist die Vorgabe — lieber eine ehrliche Lücke als eine
// erfundene Dauer.

let beaconUrl: string | null = null;
let beaconKey: string | null = null;
let beaconToken: string | null = null;

/**
 * Zugangsdaten aus der Umgebung — dieselben beiden Variablen wie in
 * `supabaseClient.ts`. Bewusst hier noch einmal gelesen statt von dort
 * exportiert: der Beacon ist der einzige Weg im Projekt, der an PostgREST
 * vorbei am Client vorbeigeht, und er soll den gemeinsamen Client nicht
 * umbauen.
 */
const readEnv = (name: string): string | undefined => {
    try {
        const viteEnv = (typeof import.meta !== 'undefined' && (import.meta as any).env)
            ? (import.meta as any).env
            : undefined;
        return viteEnv?.[name] || (typeof process !== 'undefined' ? (process.env as any)?.[name] : undefined);
    } catch {
        return undefined;
    }
};

/**
 * Das Token frisch HALTEN, nicht nur einmal vorwärmen.
 *
 * Ein Supabase-Zugangstoken hält rund eine Stunde. Vorgewärmt wird es beim
 * App-Start und bei jedem Öffnen eines Projekts — ausgerechnet die LANGEN
 * Sitzungen, deren Dauer am meisten wert ist, liefen damit beim Entladen mit
 * einem abgelaufenen Token in ein 401, und die Sitzung bliebe in der
 * Auswertung dauerhaft „offen".
 *
 * Nachgezogen wird über `onAuthStateChange` statt über einen eigenen Takt:
 * der Client erneuert das Token ohnehin selbst (`autoRefreshToken: true`,
 * supabaseClient.ts) und meldet jede Erneuerung als `TOKEN_REFRESHED`. Hier
 * wird nur zugehört und der neue Wert synchron weggeschrieben. Das trifft
 * den Zeitpunkt exakt, statt ihn im 45-Sekunden-Takt zu erraten, es braucht
 * kein `setInterval`, das jemand wieder abräumen müsste — und vor allem
 * fasst es KEINEN bestehenden Effekt in App.tsx an. Der Herzschlag der
 * Projektsperre (LOCK_HEARTBEAT_MS) wäre der falsche Anker: er läuft nur in
 * den Projektansichten und endet, sobald jemand in die Rechnungen oder die
 * Projektliste wechselt — das Projekt bliebe dabei offen, das Token liefe
 * trotzdem ab.
 *
 * Beim Abmelden kommt die Meldung ohne Sitzung. Dann fällt das Token auf
 * `null`, und der Beacon versucht es gar nicht erst mit einem toten Token.
 *
 * ─── Zweite Aufgabe desselben Abos: der Nutzerwechsel ──────────────────────
 * Dieselbe Meldung trägt den angemeldeten Nutzer. Wechselt er, muss die
 * Erfassung schneiden (`applyUserSwitch`) — und das möglichst früh, nämlich
 * schon bei der Anmeldung und nicht erst, wenn das Profil des neuen Nutzers
 * geladen ist. Dafür wird bewusst KEIN zweites Abo und kein neuer Effekt in
 * App.tsx aufgemacht: die Meldung liegt hier ohnehin auf dem Tisch.
 */
let authSubscribed = false;

const subscribeAuthChanges = (): void => {
    if (authSubscribed) return;
    authSubscribed = true;
    try {
        // Die Subscription wird bewusst nicht behalten: diese Erfassung lebt
        // so lange wie der Tab, abbestellt würde sie nie. Im nicht
        // konfigurierten Mock-Client (supabaseClient.ts) ist das ein No-op.
        supabase.auth.onAuthStateChange((_event: string, session: any) => {
            try {
                const token = session?.access_token;
                beaconToken = typeof token === 'string' && token ? token : null;
            } catch {
                /* stumm — ohne Token gibt es eben keinen Beacon */
            }
            try {
                const nextUserId = session?.user?.id;
                const marked = markedActivityUser();
                // Das Abmelden allein dreht nichts (`session` ist dann leer):
                // kommt derselbe Nutzer zurück, ist es dieselbe Browser-
                // Sitzung derselben Person und bleibt bei einem Start. Erst
                // die Anmeldung eines ANDEREN schneidet.
                if (typeof nextUserId === 'string' && nextUserId && marked && marked !== nextUserId) {
                    applyUserSwitch();
                }
            } catch {
                /* stumm — der Schnitt holt `recordAppStart` sonst nach */
            }
        });
    } catch {
        /* stumm — Zuhören ist Kür, nicht Pflicht */
    }
};

const warmBeaconAuth = (): void => {
    try {
        // Ab jetzt kommen Token-Erneuerung und Nutzerwechsel von selbst
        // herein. Das steht VOR der Prüfung der Zugangsdaten: der
        // Nutzerwechsel muss auch dann geschnitten werden, wenn für den Beacon
        // gar keine Umgebungsvariablen bereitstehen.
        subscribeAuthChanges();
        if (!beaconUrl) beaconUrl = readEnv('VITE_SUPABASE_URL') || null;
        if (!beaconKey) {
            beaconKey = readEnv('VITE_SUPABASE_KEY') || readEnv('VITE_SUPABASE_ANON_KEY') || null;
        }
        if (!beaconUrl || !beaconKey) return;
        void Promise.resolve(supabase.auth.getSession())
            .then((res: any) => {
                const token = res?.data?.session?.access_token;
                if (token) beaconToken = token;
            })
            .catch(() => { /* ohne Token gibt es eben keinen Beacon */ });
    } catch {
        /* stumm — Vorwärmen ist Kür, nicht Pflicht */
    }
};

/**
 * Schließen beim Entladen der Seite (`pagehide`).
 *
 * Nutzt dieselbe Wache wie `recordProjectClose` — ist das Projekt über den
 * Zurück-Knopf oder das Abmelden schon geschlossen worden, passiert hier
 * nichts mehr.
 */
export const recordProjectCloseBeacon = (reason: UserActivityCloseReason = 'unload'): void => {
    try {
        const held = openProject;
        if (!held) return;
        // Für den Fall, dass die Seite nur eingefroren wird statt zu sterben:
        // was hier geschlossen wird, darf beim Wiederauftauchen als NEUES
        // Öffnen wieder aufleben (`recordProjectRestore`).
        restorableProject = held;
        const occurredAt = nextOccurredAt();
        if (!beaconUrl || !beaconKey || !beaconToken || typeof fetch !== 'function') {
            // Kein Beacon möglich: der gewöhnliche Weg ist immer noch besser
            // als nichts — beim Wegwischen einer mobilen App (Seite wandert in
            // den Back-Forward-Cache, statt zu sterben) kommt er sogar an.
            // Das Journal bleibt dabei ausdrücklich LIEGEN ('stamp'): ein
            // Insert beim Entladen ist genau der Weg, der reihenweise abbricht
            // — dafür gibt es den Nachtrag.
            openProject = null;
            emitClose(held, reason, occurredAt, 'stamp');
            return;
        }
        openProject = null;
        stopHeartbeat();
        patchPending({ beaconAt: occurredAt });
        const body = JSON.stringify({
            session_id: getActivitySessionId(),
            event_type: 'project_close',
            occurred_at: occurredAt,
            project_id: held.projectId,
            project_number: held.projectNumber,
            project_name: held.projectName,
            close_reason: reason,
        });
        void fetch(`${beaconUrl}/rest/v1/user_activity_events`, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                'apikey': beaconKey,
                'Authorization': `Bearer ${beaconToken}`,
                // Ohne `return=minimal` schickt PostgREST die angelegte Zeile
                // zurück — Antwortdaten, die beim Entladen niemand mehr liest.
                'Prefer': 'return=minimal',
            },
            body,
        }).catch(() => { /* der Tab ist ohnehin weg */ });
    } catch {
        /* stumm: an dieser Stelle gibt es keine Konsole mehr, die zuhört */
    }
};

/**
 * Die Seite ist zurück — `pageshow` mit `event.persisted`.
 *
 * `pagehide` feuert nicht nur beim Sterben der Seite, sondern auch, wenn der
 * Browser sie bloß einfriert: Zurück-Navigation, weggewischte App auf iOS,
 * Tabwechsel im Back-Forward-Cache. Das Schließen ist dann bereits
 * geschrieben und die Wache leer — und ohne Gegenstück bliebe die restliche
 * Zeit im Projekt unsichtbar, während das spätere ECHTE Verlassen auf eine
 * leere Wache träfe und gar nichts mehr schriebe.
 *
 * Deshalb wird hier ein NEUES Öffnen gemeldet statt die alte Sitzung
 * heimlich weiterlaufen zu lassen. Der Back-Forward-Cache stellt den
 * kompletten JS-Zustand wieder her, der Anwender sitzt also unverändert im
 * selben Projekt — und aus der Pause werden zwei ehrlich abgegrenzte
 * Sitzungen statt einer Dauer, die die Zeit im Hintergrund mitzählt oder
 * (schlimmer) geschätzt werden müsste.
 *
 * Die Wache gegen doppelte Schließen-Ereignisse hält dabei unverändert: das
 * Merkzeichen war beim Entladen geleert worden, es wird hier über den
 * normalen `recordProjectOpen`-Weg genau einmal wieder gesetzt, und
 * `restorableProject` wird VOR dem Öffnen geleert — ein zweites `pageshow`
 * findet nichts mehr vor. Liegt wider Erwarten schon wieder ein Projekt
 * offen, passiert gar nichts: erfunden wird hier nichts.
 *
 * ─── Warum die Zeile `open_reason: 'resume'` trägt ─────────────────────────
 * Auf iOS und Android feuert `pagehide`/`pageshow(persisted)` bei JEDEM
 * Wechsel in eine andere App. Ohne Kennzeichen zählte die Auswertung diese
 * Rückkehr wie eine Projektöffnung — aus einem Vormittag mit einem Projekt
 * und acht Telefonaten würden neun „Projekte geöffnet", und dieselbe Zahl
 * verschöbe Stundenhistogramm und Heatmap gleich mit. Die SITZUNG bleibt
 * eine echte neue Sitzung (nur so kommt man ohne Schätzung der Pause aus),
 * gezählt wird sie als Öffnung aber nicht.
 *
 * Die verbrachte ZEIT zählt weiterhin voll — sie ist ja verbracht worden.
 * Diese Trennung macht `userActivityStats.ts`; hier wird nur der Grund
 * festgehalten.
 */
export const recordProjectRestore = (): void => {
    try {
        const restorable = restorableProject;
        restorableProject = null;
        if (!restorable) return;
        if (openProject) return;
        recordProjectOpen({
            projectId: restorable.projectId,
            projectNumber: restorable.projectNumber,
            projectName: restorable.projectName,
        }, 'resume');
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

/**
 * Die App ist in den Hintergrund gegangen (`visibilitychange` → hidden).
 *
 * MOBIL IST DAS DAS EIGENTLICHE VERLASSEN. Fast niemand tippt auf dem Handy
 * den Zurück-Knopf; man wechselt in die Kamera, ans Telefon oder sperrt den
 * Bildschirm — und von dort kommt die Seite oft nicht zurück, sondern wird
 * vom Betriebssystem abgeräumt. `pagehide` feuert dabei nicht verlässlich
 * (auf Android bleibt es beim reinen App-Wechsel ganz aus), `hidden` schon:
 * es ist der letzte Augenblick, in dem die Seite nachweislich lebt und ein
 * gewöhnlicher Insert noch durchkommt.
 *
 * Geschlossen wird trotzdem nicht sofort, sondern erst nach HIDDEN_GRACE_MS.
 * Ein Blick in eine andere App oder ein Tabwechsel am Schreibtisch ist kein
 * Verlassen — ohne Karenzzeit zerfiele jede Sitzung in Schnipsel. Der
 * Zeitpunkt der Zeile ist dann der WEGWECHSEL, nicht das Zuschlagen des
 * Weckers: die Karenzzeit selbst war keine Arbeitszeit.
 *
 * Zwei Wege führen von hier zum Schließen, und beide sind nötig:
 *  - der Wecker, wenn die Seite im Hintergrund weiterlaufen darf (Desktop);
 *  - die Rückkehr (`recordProjectVisible`), wenn sie eingefroren war und der
 *    Wecker gar nicht laufen konnte — mobil der Normalfall.
 * Stirbt die Seite dazwischen, trägt der nächste App-Start das Schließen aus
 * dem Journal nach, mit genau diesem `hiddenAt`.
 */
export const recordProjectHidden = (): void => {
    try {
        if (!openProject) return;
        if (hiddenTimer) return; // schon gemerkt, der Wecker läuft
        const at = nextOccurredAt();
        patchPending({ hiddenAt: at });
        hiddenTimer = setTimeout(() => {
            hiddenTimer = null;
            try {
                const held = openProject;
                if (!held) return;
                // Zwischendurch zurückgekehrt und gleich wieder weg? Dann hat
                // `recordProjectVisible` den Wecker abgestellt; steht die Seite
                // trotzdem hier, ist sie noch immer im Hintergrund.
                if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
                restorableProject = held;
                openProject = null;
                emitClose(held, 'hidden', at, 'clear');
            } catch (e) {
                console.warn('Aktivitaet nicht erfasst', e);
            }
        }, HIDDEN_GRACE_MS);
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};

/**
 * Die App ist wieder da (`visibilitychange` → visible).
 *
 * Drei Lagen, in dieser Reihenfolge:
 *
 * 1. Kurz weg gewesen (innerhalb der Karenzzeit): gar nichts schreiben. Es
 *    ist dieselbe Sitzung, der Anwender war nur eben am Telefon.
 * 2. Lang weg gewesen, und der Wecker konnte nicht schlagen, weil die Seite
 *    eingefroren war — mobil der Normalfall. Dann wird jetzt nachgeholt, was
 *    er getan hätte: Schließen auf den Zeitpunkt des Wegwechsels, danach ein
 *    Öffnen als WIEDERAUFNAHME. Ohne das zählte die Pause als Verweildauer
 *    mit, und genau das soll diese Statistik nicht.
 * 3. Das Projekt ist bereits geschlossen (Wecker oder `pagehide` waren
 *    schneller): nur die Wiederaufnahme.
 */
export const recordProjectVisible = (): void => {
    try {
        if (hiddenTimer) {
            clearTimeout(hiddenTimer);
            hiddenTimer = null;
        }
        const held = openProject;
        if (!held) {
            // Fall 3 — `recordProjectRestore` prüft selbst, ob es etwas
            // wiederaufzunehmen gibt, und erfindet nichts.
            recordProjectRestore();
            return;
        }
        const hiddenAt = pending?.hiddenAt || null;
        const weg = hiddenAt ? Date.now() - Date.parse(hiddenAt) : NaN;
        // Fall 1. Auch eine unlesbare Marke landet hier (NaN) — dann lieber
        // die Sitzung weiterlaufen lassen als sie auf einen Zeitpunkt
        // schließen, den niemand kennt.
        if (!(weg > HIDDEN_GRACE_MS)) {
            patchPending({ hiddenAt: null, lastSeenAt: new Date().toISOString() });
            startHeartbeat();
            return;
        }
        // Fall 2.
        restorableProject = held;
        openProject = null;
        emitClose(held, 'hidden', hiddenAt as string, 'clear');
        recordProjectRestore();
    } catch (e) {
        console.warn('Aktivitaet nicht erfasst', e);
    }
};
